
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { SparklesIcon } from '@heroicons/react/outline';
import { ChevronRightIcon } from '@heroicons/react/solid';
import { GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageType } from 'types';
import clsxm from '@/lib/clsxm';
import { similaritySearchImagesV2 } from '@/data/images';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import StylePreviewTile, { isVideoUrl, StylePreviewMedia, } from '@/components/images/StylePreviewTile';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { image_styles, ImageStyleType } from '@/constants/image_styles';
import { getEmbeddingVector } from '@/utils/embeddings';
// import Banner from '@/components/atoms/Banner'; // Black Friday is over
// Dynamically load heavy components
const ImageStyles = dynamic(() => import('@/components/images/ImageStyles'), {
    ssr: false,
});
const ImageStyleGuide = dynamic(() => import('@/components/images/ImageStyleGuide'), {
    ssr: false,
});
const UserImages = dynamic(() => import('@/components/images/UserImages'), {
    ssr: false,
    loading: () => (<div className='py-16 flex justify-center'>
      <div className='animate-pulse flex space-x-4'>
        <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
        <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
        <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
      </div>
    </div>),
});
const UserImagesStatic = dynamic(() => import('@/components/images/UserImagesStatic'), {
    ssr: false,
    loading: () => (<div className='py-16 flex justify-center'>
        <div className='animate-pulse flex space-x-4'>
          <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
          <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
          <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
        </div>
      </div>),
});
const ImageStyleUseCases = dynamic(() => import('@/components/ImageStyleUseCases'), {
    ssr: false,
});
const ImageStyleVideoGuide = dynamic(() => import('@/components/images/ImageStyleVideoGuide'), {
    ssr: false,
});
interface ImageGeneratorProps {
    style: ImageStyleType;
    images: ImageType[];
}
const ImageGenerator = ({ style, images }: ImageGeneratorProps) => {
    const { t, lang } = useLocale('ai-image-generator');
    // Styles whose previews are clips get the announcement pill and the
    // image-to-video guide; everything else keeps the plain image page.
    const hasVideoPreview = !!style.images?.some((image) => isVideoUrl(image.url));
    const [showSticky, setShowSticky] = useState(false);
    const [showUserImages, setShowUserImages] = useState(false);
    const userImagesRef = useRef<HTMLDivElement>(null);
    // Memoize components to prevent unnecessary renders
    const MemoizedUserImages = useCallback(() => (<UserImages initialImages={images} currentImage='0' useLoadMore={true} searchQuery={style.search_query || style.name}/>), [images, style.name]);
    const MemoizedUserImagesStatic = useCallback(() => <UserImagesStatic initialImages={images}/>, [images]);
    useEffect(() => {
        // Throttling function to limit how often the scroll handler fires
        const throttle = (callback: Function, delay: number) => {
            let lastCall = 0;
            return function (...args: any[]) {
                const now = new Date().getTime();
                if (now - lastCall >= delay) {
                    lastCall = now;
                    callback(...args);
                }
            };
        };
        const handleScroll = throttle(() => {
            // Show sticky section after scrolling 2 viewport heights
            const scrollThreshold = window.innerHeight * 1.3;
            const shouldShow = window.scrollY > scrollThreshold;
            // Only update state if the value is changing
            if (shouldShow !== showSticky) {
                setShowSticky(shouldShow);
            }
        }, 100); // Throttle to run max once every 100ms
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [showSticky]);
    // Intersection Observer for UserImages section
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setShowUserImages(true);
                observer.disconnect();
            }
        }, {
            rootMargin: '200px 0px', // Start loading when within 200px of viewport
            threshold: 0.1,
        });
        if (userImagesRef.current) {
            observer.observe(userImagesRef.current);
        }
        return () => observer.disconnect();
    }, []);
    return (<main className='relative bg-gray-100 dark:bg-slate-900'>
      {/* <Banner /> Black Friday is over */}
      <Seo templateTitle={t(`${t('AI')} ${t(style.name)} ${t(style?.skipTitle
            ? ''
            : style?.isPhoto
                ? 'Photos'
                : style?.isArt
                    ? 'Art'
                    : 'Image')} ${t('Generator')}${style?.additional_title || ''}`)} description={t(`${style.seo_description}`)} image={(!isVideoUrl(style.images[0].url) && style.images[0].url) ||
            `https://easy-peasy.ai/api/og-template?title=${t(`${t('AI')} ${t(style.name)} ${t(style?.isArt ? 'Art' : 'Image')} ${t('Generator')}`)}`}/>

      <main className='bg-white dark:bg-slate-900'>
        <LandingPageNavigation skipUserContext/>

        <div className='relative isolate overflow-hidden pt-14'>
          {/* Modern dot pattern background */}
          <div className='absolute inset-0 -z-10 bg-[radial-gradient(#80808033_1px,transparent_1px)] bg-[size:20px_20px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]'></div>

          <div className='absolute left-1/2 top-0 -z-10 -translate-x-1/2 transform-gpu blur-3xl' aria-hidden='true'>
            <div className='aspect-[1108/632] w-[69.25rem] bg-gradient-to-tr from-primary-400 to-indigo-400 opacity-20' style={{
            clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
        }}/>
          </div>
          <div className='overflow-hidden'>
            <div className={clsxm('mx-auto max-w-7xl px-6 pb-16 pt-20 lg:px-8 lg:pt-32', style.images?.length === 1
            ? 'sm:pb-20 sm:pt-28 lg:pb-32'
            : 'sm:pb-32 sm:pt-60')}>
              <div className='mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-center'>
                <div className={clsxm('w-full max-w-xl lg:shrink-0 xl:max-w-2xl', style.images?.length === 1 &&
            'mx-auto text-center lg:mx-0 lg:text-left')}>
                  {hasVideoPreview && (<Link href='/ai-animate-image' className='group mb-6 inline-flex items-center gap-x-2 rounded-full bg-white/70 py-1 pl-1 pr-3 text-sm shadow-sm ring-1 ring-inset ring-gray-900/10 backdrop-blur transition-colors hover:ring-primary-500/40 dark:bg-slate-800/70 dark:ring-white/10'>
                      <span className='rounded-full bg-primary-600 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-white'>
                        {t('New')}
                      </span>
                      <span className='font-medium text-gray-700 dark:text-slate-200'>
                        {t('Turn your images into video')}
                      </span>
                      <ChevronRightIcon className='h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5 dark:text-slate-400' aria-hidden='true'/>
                    </Link>)}
                  <h1 className='text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-6xl'>
                    {t(`${t('AI')} ${t(style.name)} ${t(style?.skipTitle
            ? ''
            : style?.isPhoto
                ? 'Photos'
                : style?.isArt
                    ? 'Art'
                    : 'Image')} ${t('Generator')}`)}
                  </h1>
                  <p className='relative mt-6 hidden text-lg leading-8 text-gray-600 dark:text-slate-300 sm:block sm:max-w-md lg:max-w-none'>
                    {/* {t(
          'Envision your idea, then bring it to life. Transform your words and phrases into stunning visuals with the top-tier AI image generator at your disposal.'
        )} */}
                    {t(style.description as string) ||
            t('Envision your idea, then bring it to life. Transform your words and phrases into stunning visuals with the top-tier AI image generator at your disposal.')}
                  </p>
                  <div className={clsxm('mt-10 flex items-center gap-x-6', style.images?.length === 1 &&
            'justify-center lg:justify-start')}>
                    <ButtonLink href={style.ctaUrl ||
            (style.isPhotoPack ? `/ai-images/models` : '/ai-images')} variant='dark' className='group relative overflow-hidden flex items-center gap-2 rounded-full px-8 py-4 text-lg font-semibold shadow-xl hover:-translate-y-1 hover:shadow-2xl transition-all duration-300' onClick={() => {
            // Save the first image's prompt to localStorage
            if (!style.ctaUrl && style.images?.[0]?.prompt) {
                localStorage.setItem('savedImagePrompt', style.images[0].prompt);
            }
        }}>
                      {t('Get started for free')}
                      <svg className='h-5 w-5 transition-transform group-hover:translate-x-1' fill='none' viewBox='0 0 24 24' strokeWidth='2' stroke='currentColor'>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3'/>
                      </svg>
                    </ButtonLink>
                    <span className='hidden text-sm font-semibold leading-6 text-gray-500 sm:block'>
                      {t('No credit card required')}
                    </span>
                  </div>
                </div>
                <div className={clsxm('flex gap-8 lg:mt-0 lg:pl-0', style.images?.length === 1
            ? 'mt-8 justify-center sm:mt-12 sm:justify-center lg:justify-end'
            : 'mt-14 justify-end sm:-mt-44 sm:pl-20')}>
                  {style.images?.length === 1 ? (<div className='mx-auto w-72 flex-none sm:mx-auto sm:w-[26rem] lg:mx-0 lg:ml-auto lg:w-[32rem]'>
                      <StylePreviewTile src={style.images[0].url} alt={style.images[0].prompt}/>
                    </div>) : style.images?.length === 2 ? (<>
                      <div className={clsxm('w-44 sm:w-60', 'ml-auto flex-none space-y-8 pt-32 sm:ml-0 sm:pt-52 lg:order-last lg:pt-0 xl:order-none')}>
                        {style.images[0].url && (<StylePreviewTile src={style.images[0].url} alt={style.images[0].prompt}/>)}
                      </div>
                      <div className='mr-auto w-44 sm:w-60 flex-none space-y-8 pt-0 sm:mr-0'>
                        {style.images[1].url && (<StylePreviewTile src={style.images[1].url} alt={style.images[1].prompt}/>)}
                      </div>
                    </>) : (<>
                      <div className={clsxm(style.images?.length === 1 ? 'w-64 xl:w-96' : 'w-44', 'ml-auto flex-none  space-y-8 pt-32 sm:ml-0 sm:pt-52 lg:order-last lg:pt-0 xl:order-none ')}>
                        {style.images?.length && style.images[0].url && (<StylePreviewTile src={style.images[0].url} alt={style.images[0].prompt}/>)}
                        {style.images?.length &&
                style.images?.length > 1 &&
                style.images[1].url && (<StylePreviewTile src={style.images[1].url} alt={style.images[1].prompt}/>)}
                      </div>
                      <div className='mr-auto w-44 flex-none space-y-8 pt-0 sm:mr-0'>
                        {style.images?.length &&
                style.images?.length > 2 &&
                style.images[2].url && (<StylePreviewTile src={style.images[2].url} alt={style.images[2].prompt}/>)}
                        {style.images?.length &&
                style.images?.length > 3 &&
                style.images[3].url && (<StylePreviewTile src={style.images[3].url} alt={style.images[3].prompt}/>)}
                      </div>
                    </>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <LogosCloud />
      {lang === 'en' && <ImageStyleUseCases style={style}/>}
      <div ref={userImagesRef} className='min-h-[300px] relative bg-white dark:bg-slate-900'>
        {!showUserImages && (<div className='py-12'>
            <div className='mx-auto max-w-7xl px-6 lg:px-8'>
              <div className='text-center'>
                <h2 className='text-3xl font-bold tracking-tight text-primary-600 sm:text-4xl mb-12'>
                  {t('Examples of Generated Art & Images')}
                </h2>
                <div className='animate-pulse flex flex-wrap justify-center gap-6'>
                  {[...Array(6)].map((_, i) => (<div key={i} className='rounded-lg bg-gray-200 dark:bg-gray-700 h-64 w-64'></div>))}
                </div>
              </div>
            </div>
          </div>)}
        {showUserImages && (<>
            {lang === 'en' ? (<MemoizedUserImages />) : (<MemoizedUserImagesStatic />)}
          </>)}
      </div>
      {lang === 'en' && <ImageStyleGuide style={style}/>}
      {lang === 'en' && hasVideoPreview && (<ImageStyleVideoGuide style={style}/>)}
      <ImageStyles showImages={false}/>
      {/* <ImageGeneratorFeatures /> */}
      <FeaturedLogos />
      <Testimonials />
      {/* <ContactUs /> */}
      <CallToAction preset='ai-images' href={style.ctaUrl || ''}/>

      {/* Sticky CTA Section */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 ${showSticky ? 'translate-y-0' : 'translate-y-full'} will-change-transform transition-transform duration-300 ease-in-out`}>
        <div className='border-t border-gray-200 bg-white/95 shadow-lg backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800/95'>
          <div className='mx-auto max-w-4xl px-4 py-3 sm:px-6 lg:px-8'>
            <div className='flex flex-col items-center gap-4 sm:flex-row sm:justify-between'>
              <div className='hidden sm:block'>
                <StylePreviewMedia src={style.images[0].url} alt={`${t(style.name)} ${t(style?.isArt ? 'Art' : 'Image')} Creation`} width={88} height={88} className='h-[88px] w-[88px] rounded-lg object-cover' loading='lazy'/>
              </div>
              <div className='flex-1 text-center sm:mx-6 sm:text-left'>
                <h2 className='text-lg font-semibold text-gray-900 dark:text-slate-200 sm:text-xl'>
                  {t('Create Your Own')} {t(style.name)}{' '}
                  {t(style?.isArt ? 'art' : 'images')}
                </h2>
                <p className='mt-1 text-sm text-gray-600 dark:text-slate-400'>
                  {t('Generate stunning {style} {type} with our easy-to-use AI generator', {
            style: t(style.name),
            type: t(style?.isArt ? 'art' : 'images'),
        })}
                </p>
              </div>
              <div className='w-full flex-shrink-0 sm:w-auto mt-4 sm:mt-0'>
                <ButtonLink href={style.ctaUrl ||
            (style.isPhotoPack ? `/ai-images/models` : '/ai-images')} variant='dark' className='group relative flex w-full items-center justify-center rounded-full sm:w-auto px-8 py-3 shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300' onClick={() => {
            // Save the first image's prompt to localStorage
            if (!style.ctaUrl && style.images?.[0]?.prompt) {
                localStorage.setItem('savedImagePrompt', style.images[0].prompt);
            }
        }}>
                  <SparklesIcon className='mr-2 h-5 w-5 transition-transform group-hover:rotate-12'/>
                  {t('Start Creating Now')}
                </ButtonLink>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </main>);
};
export default memo(ImageGenerator);
export async function getStaticPaths() {
    return {
        paths: [
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'en',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'ja',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'es',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'fr',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'it',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'de',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'ko',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'zh',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'ar',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'th',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'pt',
                };
            }),
            ...image_styles.map((style) => {
                return {
                    params: { style: style.slug },
                    locale: 'ms',
                };
            }),
        ],
        fallback: false,
    };
}
const getStaticProps = async (ctx: GetStaticPropsContext) => {
    const style = ctx?.params?.style as string;
    const styleInfo = image_styles.find((s) => s.slug === style);
    const lang = ctx.locale || 'en';
    if (!styleInfo) {
        return {
            notFound: true,
        };
    }
    let images: Partial<ImageType>[] = [];
    // get search embeding
    const embedding = await getEmbeddingVector(styleInfo.search_query || styleInfo.name, 'text-embedding-3-small');
    // if image has embedding, get similar images
    images = await similaritySearchImagesV2(0, embedding, 1);
    return {
        props: {
            style: styleInfo,
            images,
        },
    };
};

    async function __Next_Translate__getStaticProps__1a12e86ef81__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-image-generator/[style]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a12e86ef81__ as getStaticProps }
  